import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Tooltip,
  Button,
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import {
  Menu as MenuIcon,
  Mic as MicIcon,
  ImportContacts as BookIcon,
  TextFields as TextFieldsIcon,
  BarChart as BarChartIcon,
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { authApi } from '../services/api';

const expandedDrawerWidth = 240;
const collapsedDrawerWidth = 72;

const menuItems = [
  { text: 'Детекции', icon: <BookIcon />, path: '/detections' },
  { text: 'Текстовые шаблоны', icon: <TextFieldsIcon />, path: '/patterns' },
  { text: 'Голосовые шаблоны', icon: <RecordVoiceOverIcon />, path: '/embeddings' },
  { text: 'Статистика', icon: <BarChartIcon />, path: '/statistics' },
];

interface LayoutProps {
  children: React.ReactNode;
  mode?: 'light' | 'dark';
  onToggleTheme?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, mode = 'dark', onToggleTheme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const tokens = authApi.getTokens();

  const handleLogout = async () => {
    await authApi.logout();
    navigate('/login', { replace: true });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    if (isMobile) {
      setMobileOpen(false);
      return;
    }
    setIsCollapsed((prev) => !prev);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <div>
      <Toolbar sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between', pl: 2, pr: isCollapsed ? 0 : 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Box
            component="img"
            src="/s2s-next-logo.png"
            alt="S2S Next"
            sx={{ height: 32, width: 'auto', mr: 1.25 }}
          />
          {!isCollapsed && (
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Poppins, Inter, Roboto, sans-serif',
                fontWeight: 700,
                letterSpacing: 0.6,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              S2S Next
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleCollapseToggle}
          size="small"
          aria-label={isMobile ? 'Закрыть панель' : (isCollapsed ? 'Развернуть панель' : 'Свернуть панель')}
          sx={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: isMobile ? 1 : (isCollapsed ? 0 : 1),
            transition: 'opacity 120ms ease',
          }}
        >
          {isMobile ? <ChevronLeftIcon /> : (isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />)}
        </IconButton>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              className="interactive-element"
              sx={{
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 2 : 3,
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                <Tooltip title={isCollapsed ? item.text : ''} placement="right">
                  <Box component="span">{item.icon}</Box>
                </Tooltip>
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${isCollapsed ? collapsedDrawerWidth : expandedDrawerWidth}px)` },
          ml: { sm: `${isCollapsed ? collapsedDrawerWidth : expandedDrawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className="interactive-element"
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: { xs: 16, sm: 18 } }}>
            Система обнаружения автоответчиков
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {!tokens ? (
              <Button size="small" color="inherit" variant="outlined" startIcon={<LoginIcon />} onClick={() => navigate('/login')}>
                Войти
              </Button>
            ) : (
<IconButton size="small" color="inherit" onClick={handleLogout}>
  <LogoutIcon />
</IconButton>
            )}
            <Tooltip title={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}>
              <IconButton color="inherit" onClick={onToggleTheme} aria-label="toggle theme">
                {mode === 'dark' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: isCollapsed ? collapsedDrawerWidth : expandedDrawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: expandedDrawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: isCollapsed ? collapsedDrawerWidth : expandedDrawerWidth, overflowX: 'hidden' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        className="scroll-container performance-optimized"
        data-scroll-container
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${isCollapsed ? collapsedDrawerWidth : expandedDrawerWidth}px)` },
          overflow: { xs: 'auto', sm: 'visible' },
          '-webkit-overflow-scrolling': { xs: 'touch', sm: 'auto' },
          touchAction: { xs: 'pan-y', sm: 'auto' },
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ 
          maxWidth: 1200, 
          mx: 'auto', 
          width: '100%', 
          px: { xs: 1.5, sm: 2 },
          minHeight: { xs: 'calc(100vh - 64px)', sm: 'auto' },
          overflow: 'visible',
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
